import { AnimatePresence, motion } from 'motion/react';
import { createPortal } from 'react-dom';
import { Observable, delay, fromEvent, merge } from 'rxjs';

import { useMergedRef, useSubscriptions } from '@/hook';
import {
  getAncestorList,
  getScrollableAncestorList,
  setStyleProperties,
} from '@/util';

import { new__cn as cn } from '../util';
import { DEFAULT_POPOVER_DURATION } from './constant';
import { useVBasePopoverContext } from './hook';
import style from './style';
import { getChildren, getPosition } from './util';

export const VBaseInnerPopover = ({
  children,
  ref,
}: React.PropsWithChildren & React.RefAttributes<HTMLDivElement>) => {
  const {
    className,
    content,
    duration = DEFAULT_POPOVER_DURATION,
    noUsePortal,
    placement,
    style: _style,
    visible,
    setRef,
    $popover,
    $target,
  } = useVBasePopoverContext();
  const mergedRef = useMergedRef($popover, ref as any);
  const portalContent = (
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={mergedRef}
          role="tooltip"
          aria-hidden={!visible}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: duration / 1000,
          }}
          style={_style}
          className={cn(style.vPopover, className)}
        >
          {content()}
        </motion.div>
      )}
    </AnimatePresence>
  );

  const attachRef = (node: HTMLElement | null) => {
    setRef('$target', node);

    const childRef = (children as any).props.ref as React.Ref<HTMLElement>;

    if (typeof childRef === 'function') {
      childRef(node);
    } else if (childRef && Object.hasOwn(childRef, 'current')) {
      // eslint-disable-next-line react-hooks/immutability
      childRef.current = node;
    }
  };

  const updatePosition = () => {
    const target = $target.current;
    const popover = $popover.current;

    if (!target || !popover) return;

    const position = getPosition({
      placement,
      $popover: popover,
      $target: target,
    });

    const newTop = `${position.top}px`;
    const newLeft = `${position.left}px`;

    // Safari/Firefox에서 무한 루프 방지를 위해 값이 변경된 경우에만 스타일 적용
    if (
      popover.style.getPropertyValue('--popover-top') !== newTop ||
      popover.style.getPropertyValue('--popover-left') !== newLeft
    ) {
      setStyleProperties(popover, {
        '--popover-top': newTop,
        '--popover-left': newLeft,
      });
    }
  };

  useSubscriptions(() => {
    if (!visible) return [];

    const popover = $popover.current;
    const target = $target.current;

    if (!popover || !target) return [];

    updatePosition();

    const scrollableAncestorList = getScrollableAncestorList(target);
    const resize$ = fromEvent(window, 'resize');
    const scroll$ = merge(
      ...scrollableAncestorList.map(el =>
        fromEvent(el, 'scroll', { passive: true }),
      ),
    );

    const ancestorResize$ = new Observable(subscriber => {
      const resizeObserver = new ResizeObserver(() => subscriber.next(null));

      // popover 자체는 관찰하지 않음 (Safari/Firefox에서 무한 루프 방지)
      resizeObserver.observe(target);

      getAncestorList(target).forEach(el => resizeObserver.observe(el));

      return () => resizeObserver.disconnect();
    });

    const merged$ = merge(resize$, scroll$, ancestorResize$);

    return [merged$.pipe(delay(16)).subscribe(updatePosition)];
  }, [visible, placement]);

  return (
    <>
      {noUsePortal ? portalContent : createPortal(portalContent, document.body)}
      {getChildren(children, attachRef)}
    </>
  );
};

VBaseInnerPopover.displayName = 'VBaseInnerPopover';
